import './marquee.scss';

/**
 * Represents a Marquee component that animates items within a container.
 */
export class Marquee {
  /**
   * Creates an instance of Marquee.
   * @param {HTMLElement} element - The DOM element that contains the marquee.
   * @param {Object} userConfig
   * @param {HTMLElement} userConfig.innerElement - The DOM element that represent the marquee inner.
   * @param {HTMLElement} userConfig.itemElement - The DOM element that represent the marquee item.
   * @param {boolean} [userConfig.pauseOnHover=false] - Whether to pause the animation on hover.
   * @param {boolean} [userConfig.autoStart=true] - Whether to start the animation automatically.
   * @param {number} [userConfig.speed=40] - The speed of the animation in pixel per seconds.
   * @param {string} [userConfig.blockClass='marquee'] - The class name of the marquee BEM-Block.
   * @memberof Marquee
   */
  constructor(element, userConfig) {
    this.element = element;

    this.blockClass = userConfig.blockClass || 'marquee';

    this.innerElement =
      userConfig.innerElement ||
      this.element.querySelector(`.${this.blockClass}__inner`);
    this.itemElement =
      userConfig.itemElement ||
      this.element.querySelector(`.${this.blockClass}__item`);

    this.pauseOnHover = userConfig.pauseOnHover || false;
    this.autoStart = userConfig.autoStart || true;
    this.speed = userConfig.speed || 40;

    this.state = {};
    this.stateObserver = {};
    this.stateChangeCallback = userConfig.stateChangeCallback || false;

    this.stateClasses = {
      isRunning: `${this.blockClass}--is-running`,
      hasAnimation: `${this.blockClass}--has-animation`,
      hasPauseOnHover: `${this.blockClass}--has-pause-on-hover`,
    };

    this.cloneClasses = {
      innerClone: `${this.blockClass}__inner-clone`,
      itemClone: `${this.blockClass}__item-clone`,
    };

    this._duration = null; // set by this.setDuration()
  }

  /**
   * Initializes the Marquee component by cloning the marquee item, setting up event bindings, and triggering the animation.
   */
  init = () => {
    this.cloneItemToFillWidth();
    this.duplicateInnerElement();
    this.setDuration();
    this.pauseOnHover && this.enablePauseOnHover();
    this.bindEvents();
    this.autoStart && this.enableAutoStart();
    this.initStateObserver();
  };

  /**
   * Binds the necessary events for the Marquee component, such as load, visibility change, and resize.
   */
  bindEvents = () => {
    // When the user re-enters the page, trigger the animation again.
    document.addEventListener('visibilitychange', () => {
      if (!this.element.classList.contains(this.stateClasses.isRunning)) return;

      this.stop();
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          this.start();
        })
      );
    });

    // When the user resizes the window, reset the whole animation.
    window.addEventListener('resize', () => {
      if (!this.element.classList.contains(this.stateClasses.isRunning)) return;

      this.stop();
      this.setDuration();

      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          this.start();
        })
      );
    });
  };

  /**
   * Calculates and sets animation duration based on the speed
   * and the width of the inner element.
   */
  setDuration = () => {
    const width = this.element.clientWidth;
    const innerWidth = this.innerElement.clientWidth;
    const normalizefactor =
      innerWidth < width ? 1000 : (innerWidth / width) * 1000;

    this._duration = normalizefactor / this.speed;

    this.element.style.setProperty(
      '--animation-duration',
      `${this._duration}s`
    );
  };

  /**
   * When the marquee item is not long enough
   * we duplicated / clone it, until it is
   */
  cloneItemToFillWidth = () => {
    const containerWidth = this.element.clientWidth;
    const innerWidth = this.innerElement.clientWidth;

    const clonesNeeded = Math.floor(containerWidth / innerWidth);
    for (let i = 0; i < clonesNeeded; i++) {
      const cloneElement = this.itemElement.cloneNode(true);
      cloneElement.classList.add(this.cloneClasses.itemClone);
      cloneElement.setAttribute('aria-hidden', 'true');
      this.hideFocusablesInside(cloneElement);
      this.innerElement.appendChild(cloneElement);
    }
  };

  /**
   * We need the inner element to be duplicated
   * to show the start of the item while the end
   * is running out of the container
   */
  duplicateInnerElement = () => {
    const cloneElement = this.innerElement.cloneNode(true);
    cloneElement.classList.add(this.cloneClasses.innerClone);
    cloneElement.setAttribute('aria-hidden', 'true');
    this.hideFocusablesInside(cloneElement);
    this.element.appendChild(cloneElement);
  };

  /**
   * Hide focusables inside.
   */
  hideFocusablesInside = parent => {
    parent
      .querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      .forEach(el => {
        el.setAttribute('tabindex', '-1');
        el.setAttribute('aria-hidden', 'true');
      });
  };

  /**
   * Update marquee state object.
   */
  updateState = () => {
    const regex = new RegExp(`${this.blockClass}--`);

    this.state = Array.from(this.element.classList)
      .filter(className => {
        if (className.includes(`${this.blockClass}--`)) return true;
        return false;
      })
      .reduce(
        (obj, key) => Object.assign(obj, { [key.replace(regex, '')]: true }),
        {}
      );

    this.stateChangeCallback && this.stateChangeCallback(this.state);
  };

  /**
   * Initialize observer for attribute changes on marquee and update marquee state object.
   */
  initStateObserver = () => {
    const config = { attributes: true };
    this.stateObserver = new MutationObserver(this.updateState);
    this.stateObserver.observe(this.element, config);
  };

  /**
   * Enables the pause on hover functionality
   */
  enablePauseOnHover = () => {
    this.element.classList.add(this.stateClasses.hasPauseOnHover);
  };

  enableAutoStart = () => {
    this.start();

    window.addEventListener('load', () => {
      this.stop();
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          this.start();
        })
      );
    });
  };

  /**
   * Starts the animation.
   *
   * @public
   */
  start = () => {
    this.element.classList.add(this.stateClasses.hasAnimation);
    this.element.classList.add(this.stateClasses.isRunning);
  };

  /**
   * Stops the animation (-> and also resets it).
   *
   * @public
   */
  stop = () => {
    this.element.classList.remove(this.stateClasses.hasAnimation);
    this.element.classList.remove(this.stateClasses.isRunning);
  };

  /**
   * Pauses the animation.
   *
   * @public
   */
  pause = () => {
    this.element.classList.remove(this.stateClasses.isRunning);
  };

  /**
   * Toggle the animation.
   *
   * @public
   */
  toggle = () => {
    this.element.classList.toggle(this.stateClasses.isRunning);
  };

  /**
   * Destroys the animation.
   *
   * @public
   * @todo Fully clean up the DOM.
   */
  destroy = () => {
    this.element.classList.remove(this.stateClasses.hasAnimation);
    this.element.classList.remove(this.stateClasses.isRunning);
    this.element.style.removeProperty('--animation-duration');
    this.stateObserver.disconnect();
  };
}
